import type { AttackProfile, Item, ItemUse, ItemKind, GearSlot } from '../content/types.ts';
import type { StatId } from '../domain/stats.ts';
import { CUSTOM_CONTENT_ID_PREFIX, isCustomContentId } from '../domain/customContentId.ts';
import { itemUseDefinitionProblem } from './itemUse.ts';
import {
  MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH,
  MAX_CUSTOM_ITEM_NAME_LENGTH,
  MAX_CUSTOM_ITEM_NOTES_LENGTH,
} from '../domain/customItem.ts';
import { weaponLinkFault } from './inventory.ts';
import type { DamageType } from './damage.ts';
import { isDamageType } from './damage.ts';

/**
 * What a player may define as an Item of their own.
 *
 * The editor exposes a deliberate subset of the generic Effect engine. Anything
 * outside that subset belongs in Special Rules rather than being smuggled into
 * JSON as an executable promise the UI cannot show or repair.
 */

export type CustomItemModifier =
  | { kind: 'stat'; stat: StatId; amount: number }
  | { kind: 'skill-rank'; skillId: string; amount: number }
  | { kind: 'damage-resistance'; amount: number }
  | { kind: 'elemental-resistance'; damageType: DamageType }
  | { kind: 'vulnerability'; damageType: DamageType }
  | { kind: 'immunity'; damageType: DamageType }
  | { kind: 'evade'; amount: number }
  | { kind: 'move'; amount: number }
  | { kind: 'step'; amount: number }
  | { kind: 'max-mana'; amount: number }
  | { kind: 'advantage' }
  | { kind: 'disadvantage' };

export interface CustomItemDraft {
  goldValue?: number;
  name: string;
  kind: ItemKind;
  /** Null when carried rather than worn. */
  slot: GearSlot | null;
  /** Only meaningful in Hands/Holding. */
  handUse: 'held' | 'worn';
  /** The physical weapon Attack this Item IS, or null for explicitly nonweapon. */
  weaponAttackId: string | null;
  /** Closed executable Item-use union; null means manual/no automation. */
  use: ItemUse | null;
  consumable: boolean;
  description: string;
  /** Freeform Special Rules. Recorded for people; never parsed into mechanics. */
  notes?: string;
  modifiers: CustomItemModifier[];
}

export interface CustomItemProblem {
  field:
    | 'goldValue'
    | 'name'
    | 'kind'
    | 'slot'
    | 'handUse'
    | 'description'
    | 'notes'
    | 'modifiers'
    | 'id'
    | 'weaponAttackId'
    | 'use';
  message: string;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function checkCustomItemDraft(
  draft: CustomItemDraft,
  existingItems: readonly Item[],
  editingId: string | null = null,
  lookupAttack: (id: string) => AttackProfile | undefined = () => undefined,
  lookupSkill: (id: string) => unknown = () => undefined,
): CustomItemProblem[] {
  const problems: CustomItemProblem[] = [];
  const name = draft.name.trim();
  if (draft.goldValue !== undefined && (!Number.isSafeInteger(draft.goldValue) || draft.goldValue < 0)) problems.push({ field: 'goldValue', message: 'Gold value must be a non-negative safe whole number.' });

  const useProblem =
    draft.use === null
      ? null
      : itemUseDefinitionProblem({
          name: name === '' ? 'This Item' : name,
          kind: draft.kind,
          consumable: draft.consumable,
          use: draft.use,
        });
  if (useProblem !== null) problems.push({ field: 'use', message: useProblem });

  if (name === '') {
    problems.push({ field: 'name', message: 'An Item needs a name.' });
  } else if (name.length > MAX_CUSTOM_ITEM_NAME_LENGTH) {
    problems.push({
      field: 'name',
      message: `An Item name is at most ${MAX_CUSTOM_ITEM_NAME_LENGTH} characters.`,
    });
  } else {
    const clash = existingItems.find(
      (item) => item.id !== editingId && normalizeName(item.name) === normalizeName(name),
    );
    if (clash) {
      problems.push({
        field: 'name',
        message: isCustomContentId(clash.id)
          ? `You already have an Item called ${clash.name}.`
          : `${clash.name} is already an Item in the book. Pick a different name.`,
      });
    }
  }

  if (draft.description.length > MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH) {
    problems.push({
      field: 'description',
      message: `A description is at most ${MAX_CUSTOM_ITEM_DESCRIPTION_LENGTH} characters.`,
    });
  }
  const notes = draft.notes ?? '';
  if (notes.length > MAX_CUSTOM_ITEM_NOTES_LENGTH) {
    problems.push({
      field: 'notes',
      message: `Special Rules are at most ${MAX_CUSTOM_ITEM_NOTES_LENGTH} characters.`,
    });
  }

  // Shared physical-weapon authority. This answers whether the persisted link is
  // truthful; the UI separately owns the form-only "unanswered" state.
  const weaponFault = weaponLinkFault(draft, lookupAttack);
  if (weaponFault !== null) {
    const message: Record<typeof weaponFault, string> = {
      'unknown-attack': `There is no Attack called "${draft.weaponAttackId}".`,
      'not-a-weapon':
        `${draft.weaponAttackId} is not made with a held weapon, so no Item can be one. ` +
        "Wrasslin' needs two hands and is still not an object you can hold.",
      'wrong-slot': 'A weapon is held in the hands. Set the Gear Slot to Hands/Holding.',
      worn: 'A weapon is held rather than worn.',
    };
    problems.push({ field: 'weaponAttackId', message: message[weaponFault] });
  }

  if (draft.slot === null && draft.modifiers.length > 0) {
    problems.push({
      field: 'modifiers',
      message:
        'Only worn or held Items give bonuses (Rules p.48). Give this a Gear Slot, or remove ' +
        'its bonuses.',
    });
  }

  for (const modifier of draft.modifiers) {
    if (modifier.kind === 'advantage' || modifier.kind === 'disadvantage') continue;

    if (
      modifier.kind === 'elemental-resistance' ||
      modifier.kind === 'vulnerability' ||
      modifier.kind === 'immunity'
    ) {
      if (!isDamageType(modifier.damageType)) {
        problems.push({ field: 'modifiers', message: 'Choose a real DCC Damage Type.' });
        break;
      }
      continue;
    }

    if (modifier.kind === 'skill-rank') {
      if (modifier.skillId.trim() === '') {
        problems.push({ field: 'modifiers', message: 'A Skill Rank modifier must name a Skill.' });
        break;
      }
      if (lookupSkill(modifier.skillId) === undefined) {
        problems.push({
          field: 'modifiers',
          message: `There is no live Skill called "${modifier.skillId}". Repair that dependency before saving.`,
        });
        break;
      }
    }

    if (!Number.isInteger(modifier.amount)) {
      problems.push({ field: 'modifiers', message: 'A bonus must be a whole number.' });
      break;
    }
    if (modifier.amount === 0) {
      problems.push({ field: 'modifiers', message: 'A bonus of 0 does nothing. Remove it.' });
      break;
    }
  }

  if (editingId !== null && !isCustomContentId(editingId)) {
    problems.push({
      field: 'id',
      message: 'Only your own Items can be edited. Items from the book are fixed.',
    });
  }

  return problems;
}

/** Turns the editor's modifier shape into the generic Effect system's shape. */
function toModifier(modifier: CustomItemModifier): Item['modifiers'][number] {
  switch (modifier.kind) {
    case 'stat':
      return { target: { kind: 'stat', stat: modifier.stat }, op: 'add', amount: modifier.amount };
    case 'skill-rank':
      return {
        target: { kind: 'skill-rank', skillId: modifier.skillId },
        op: 'add',
        amount: modifier.amount,
      };
    case 'elemental-resistance':
    case 'vulnerability':
    case 'immunity':
      return { target: { kind: modifier.kind, damageType: modifier.damageType }, op: 'grant' };
    case 'advantage':
      return { target: { kind: 'advantage', scope: { kind: 'all' } }, op: 'grant' };
    case 'disadvantage':
      return { target: { kind: 'disadvantage', scope: { kind: 'all' } }, op: 'grant' };
    default:
      return { target: { kind: modifier.kind }, op: 'add', amount: modifier.amount };
  }
}

export function buildCustomItem(draft: CustomItemDraft, id: string, now: string): Item {
  if (!id.startsWith(CUSTOM_CONTENT_ID_PREFIX)) {
    throw new Error(`A custom Item ID must begin with "${CUSTOM_CONTENT_ID_PREFIX}": got "${id}"`);
  }
  const description = draft.description.trim();
  const notes = (draft.notes ?? '').trim();
  return {
    id,
    name: draft.name.trim(),
    ...(draft.goldValue === undefined ? {} : { goldValue: draft.goldValue }),
    kind: draft.kind,
    slot: draft.slot,
    ...(draft.slot === 'hands' ? { handUse: draft.handUse } : {}),
    consumable: draft.consumable,
    ...(draft.use === null ? {} : { use: draft.use }),
    weaponAttackId:
      draft.slot === 'hands' && draft.handUse === 'held' ? draft.weaponAttackId : null,
    modifiers: draft.modifiers.map(toModifier),
    ...(description === '' ? {} : { description }),
    ...(notes === '' ? {} : { notes }),
    source: { book: 'custom', createdAt: now },
  };
}

/** Stable custom ID: editing changes the definition, not its identity. */
export function applyCustomItemEdit(existing: Item, draft: CustomItemDraft): Item {
  const createdAt = existing.source.book === 'custom' ? existing.source.createdAt : '';
  return buildCustomItem(draft, existing.id, createdAt);
}

/** Converts one supported stored generic Modifier into the custom Item subset. */
export function customItemModifierFromStored(
  modifier: Item['modifiers'][number],
): CustomItemModifier | null {
  const target = modifier.target;
  const amount = modifier.amount ?? 0;
  switch (target.kind) {
    case 'stat':
      return modifier.op === 'add' ? { kind: 'stat', stat: target.stat, amount } : null;
    case 'skill-rank':
      return modifier.op === 'add'
        ? { kind: 'skill-rank', skillId: target.skillId, amount }
        : null;
    case 'damage-resistance':
      return modifier.op === 'add' ? { kind: 'damage-resistance', amount } : null;
    case 'evade':
    case 'move':
    case 'step':
    case 'max-mana':
      return modifier.op === 'add' ? { kind: target.kind, amount } : null;
    case 'elemental-resistance':
    case 'vulnerability':
    case 'immunity':
      return modifier.op === 'grant'
        ? { kind: target.kind, damageType: target.damageType }
        : null;
    case 'advantage':
    case 'disadvantage':
      return modifier.op === 'grant' && target.scope.kind === 'all'
        ? { kind: target.kind }
        : null;
    default:
      return null;
  }
}

/**
 * Converts an Item without silently dropping mechanics. Used by core/custom
 * Duplicate-as-Custom and by the editor for stored V5 definitions.
 */
export function exactDraftFromItem(
  item: Item,
): { ok: true; draft: CustomItemDraft } | { ok: false; message: string } {
  const modifiers: CustomItemModifier[] = [];
  for (const modifier of item.modifiers) {
    const converted = customItemModifierFromStored(modifier);
    if (converted === null) {
      return {
        ok: false,
        message:
          `Cannot represent ${item.name} faithfully because it contains an executable modifier ` +
          'outside the custom Item editor\'s supported subset.',
      };
    }
    modifiers.push(converted);
  }

  return {
    ok: true,
    draft: {
      name: item.name,
      ...(item.goldValue === undefined ? {} : { goldValue: item.goldValue }),
      kind: item.kind,
      slot: item.slot,
      handUse: item.handUse ?? 'held',
      weaponAttackId: item.weaponAttackId,
      consumable: item.consumable,
      use: item.use ?? null,
      description: item.description ?? '',
      notes: item.notes ?? '',
      modifiers,
    },
  };
}

/** Stored custom V5 Items are validator-approved, so this should be lossless. */
export function draftFromCustomItem(item: Item): CustomItemDraft {
  const converted = exactDraftFromItem(item);
  if (!converted.ok) throw new Error(converted.message);
  return converted.draft;
}
